import { Checkbox } from 'components/ui/checkbox';
import React, { useEffect } from 'react';
import { cn } from 'utils/cn';
import { getSelectorKey } from '../../getSelectorKey.ts';
import {
    CheckBoxGroupFilterForm,
    FilterCheckboxInForm,
    MainFilterForm,
    QuickFilterForm,
} from '../../ListTableTypes.tsx';
import { OptionalTooltip } from '../QuickFilters.tsx';
import { isRuledOut } from '../../ruledOutOptions.ts';
import { FilterLabel } from './FilterLabel';

export const CheckboxGroup: React.FC<{
    filter: CheckBoxGroupFilterForm;
    direction?: 'column' | 'row';
    quickFilters: QuickFilterForm;
    mainFilters: MainFilterForm;
    onChange: (value: boolean, argument: string, groupSelector: string, key: string) => void;
}> = React.memo(({ filter, direction, onChange, quickFilters, mainFilters }) => {
    const isHidden =
        filter.getShouldHide !== undefined
            ? filter.getShouldHide({
                  mainFilters,
                  quickFilters,
                  groupSelector: getSelectorKey(filter),
              })
            : false;

    useEffect(() => {
        // Only when there is something to drop: `onChange` re-renders the whole drawer, so clearing an
        // already-empty filter on every render of a hidden one would loop.
        if (isHidden && !filter?.preserveValueWhenHidden && filter.filters.some((filterBox) => filterBox.isSelected)) {
            const selectorKey = getSelectorKey(filter);
            filter.filters.forEach((filterBox: FilterCheckboxInForm) =>
                onChange(false, filterBox.argument, selectorKey, filterBox.key)
            );
        }
    }, [filter, isHidden, onChange]);

    if (isHidden) {
        return null;
    }
    return (
        <div className="flex flex-col" key={`filter-${filter.selector}`}>
            {filter.label && (
                <FilterLabel label={filter.label} tooltip={filter.tooltip} tooltipValues={filter.tooltipValues} />
            )}
            <div
                className={cn('flex flex-wrap justify-start gap-x-2', direction === 'column' ? 'flex-col' : 'flex-row')}
            >
                {filter.filters.map((filterBox: FilterCheckboxInForm) => {
                    if (filterBox.isNo || isRuledOut(filterBox, mainFilters)) return null;
                    return (
                        <OptionalTooltip
                            tooltip={filterBox?.tooltip}
                            tooltipValues={filterBox?.tooltipValues}
                            key={`fg-f-${filterBox.argument}`}
                        >
                            {/* A 32px row, the checkbox 4px in from the group's edge and 8px from its label. */}
                            <label className="text-body1 flex min-h-8 w-fit cursor-pointer items-center gap-2 pr-1 pl-1 select-none">
                                <Checkbox
                                    variant="secondary"
                                    checked={filterBox.isSelected}
                                    onCheckedChange={(checked) =>
                                        onChange(checked, filterBox.argument, getSelectorKey(filter), filterBox.key)
                                    }
                                />
                                {filterBox.label}
                            </label>
                        </OptionalTooltip>
                    );
                })}
            </div>
        </div>
    );
});

type CheckBoxGroupFilterFormCommon = Pick<CheckBoxGroupFilterForm, 'filters' | 'multiple'>;

export const changeCheckBoxGroupFilterState = (
    key: string,
    value: boolean,
    checkBoxGroup: CheckBoxGroupFilterFormCommon
) => {
    checkBoxGroup.filters = checkBoxGroup.filters.map((filter: FilterCheckboxInForm) => {
        if (filter.key === key) {
            return { ...filter, isSelected: value, key: filter.key };
        }
        // If not multiple and we're checking a checkbox, uncheck all others
        if (checkBoxGroup.multiple !== undefined && !checkBoxGroup.multiple && value && filter.key !== key) {
            return { ...filter, isSelected: false, key: filter.key };
        }
        return filter;
    });
};
